import { Form, notification, Spin } from "antd";
import Search from "antd/es/input/Search";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiError, getMarketInventoryGetThunk } from "tm-shared/api";
import {
    Product,
    SortOrder,
    TaskRejectProducts,
    TaskUpdateProductBalances,
} from "tm-shared/types";

import Header from "@/components/Header";
import { Loading } from "@/components/Loading";
import Meta from "@/components/Meta";
import { SVScanButton } from "@/components/SVScanButton";
import { HeaderFilterButton } from "@/components/UI/HeaderFilterButton";
import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useQueryFilter } from "@/hooks/useQueryFilter";
import { makeCommand, useMakeCommand } from "@/hooks/useRequestQueue";
import { ConfirmActionBtn } from "@/pages/WorkPlan/Tasks/components/ConfirmActionBtn";
import {
    StyledContentContainer,
    StyledSearchWrapper,
} from "@/pages/WorkPlan/Tasks/shared";
import { getFilterCount } from "@/pages/WorkPlan/Tasks/utils";
import {
    enqueueOrReplaceRequestQueueItem,
    enqueueRequestQueueItem,
    setMarketInventories,
} from "@/store/slices/offline";
import {
    clearTaskPayload,
    setUpdateProductBalancesInventory,
} from "@/store/slices/progress";
import {
    patchProductsSelectedFilters,
    setProductsHideZero,
    setProductsShowOnlyMML,
    setProductsSortOrder,
    setProductsSortTag,
    setUpdateProductBalancesQuery,
} from "@/store/slices/ui";
import { setTaskCompleted } from "@/store/slices/work";

import { OfflineFilterDrawer } from "../components/OfflineFilterDrawer";
import { InventoryProductCard } from "./components/InventoryProductCard";

interface InventoryProductsPageProps {
    marketId: string;
    task: TaskUpdateProductBalances | TaskRejectProducts;
    isRejectTask: boolean;
}

export const InventoryProductsPage: FC<InventoryProductsPageProps> = ({
    marketId,
    task,
    isRejectTask,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const {
        updateProductBalances: { query: searchQuery },
        productsFilter: { selectedFilters },
    } = useAppSelector((s) => s.ui);

    const { updateProductBalances } = useAppSelector((s) => s.progress);
    const { offlineMode, products, marketsInventories } = useAppSelector(
        (s) => s.offline,
    );

    const [open, setOpen] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);

    const taskId = task.id;

    const currentFormValues = updateProductBalances[taskId];
    const currentMarketInventory = marketsInventories[marketId] ?? {};

    useEffect(() => {
        if (!currentFormValues) {
            dispatch(
                setUpdateProductBalancesInventory({
                    taskId,
                    remains: currentMarketInventory,
                }),
            );
        }
    }, []);

    const [sortedAndFilteredProducts, setSortedAndFilteredProducts] = useState<
        Product[]
    >(() => Object.values(products));

    const searchFilteredProducts = useQueryFilter<Product>(
        sortedAndFilteredProducts,
        searchQuery,
        [
            (p) => p?.name,
            (p) => p?.barcode,
            (p) => p?.params?.description?.value,
            (p) => p?.params?.capacity?.value,
        ],
    );

    const filterCount = useMemo(
        () => getFilterCount(selectedFilters),
        [selectedFilters],
    );

    const showDrawer = useCallback(() => setOpen(true), []);

    const onSearch = useCallback(
        (query: string) => dispatch(setUpdateProductBalancesQuery(query)),
        [dispatch],
    );

    const { makeTaskCompleteCommands } = useMakeCommand();

    const [handleConfirmTask, sendLoading] = useAsyncAction(async () => {
        const restProducts = currentFormValues;

        if (isRejectTask) {
            dispatch(
                enqueueOrReplaceRequestQueueItem({
                    id: `${taskId}-reject`,
                    metadata: { marketId, title: "Актуалізація залишків" },
                    commands: [
                        makeCommand({
                            type: "patchMarketInventorySet",
                            title: "Оновлення залишків на у базі",
                            params: { marketId, restProducts },
                        }),
                        makeCommand({
                            type: "getMarketInventoryGetThunk",
                            title: "Синхронізація залишків",
                            params: { marketId },
                        }),
                    ],
                }),
            );

            dispatch(setMarketInventories({ marketId, rest: restProducts }));
            navigate(-1);
        } else {
            const taskCompleteCommands = await makeTaskCompleteCommands({
                params: {
                    taskId,
                    hasPhoto: false,
                    payload: { restProducts },
                },
                marketId,
            });

            dispatch(
                enqueueRequestQueueItem({
                    id: taskId,
                    metadata: { marketId, taskId },
                    commands: [
                        makeCommand({
                            type: "patchMarketInventorySet",
                            title: "Оновлення залишків на у базі",
                            params: { marketId, restProducts },
                        }),

                        ...taskCompleteCommands,

                        makeCommand({
                            type: "getMarketInventoryGetThunk",
                            title: "Синхронізація залишків",
                            params: { marketId },
                        }),
                    ],
                }),
            );

            dispatch(clearTaskPayload({ taskId }));
            dispatch(setTaskCompleted({ marketId, taskId }));
            dispatch(setMarketInventories({ marketId, rest: restProducts }));
            navigate(ROUTES.WorkPlan, { replace: true });
        }
    });

    useEffect(() => {
        const fetchInitial = async () => {
            try {
                setInitialLoading(true);
                const { result } = await dispatch(
                    getMarketInventoryGetThunk({ marketId }),
                ).unwrap();

                dispatch(
                    setUpdateProductBalancesInventory({
                        taskId,
                        remains: result.rest,
                    }),
                );
            } catch (error) {
                const { displayMessage, code } = getApiError(error);
                if (code !== "-32404") {
                    notification.error({
                        message: "Не вдалося завантажити залишки",
                        description: displayMessage,
                    });
                }
            } finally {
                setInitialLoading(false);
            }
        };

        if (!offlineMode) {
            fetchInitial();
        }

        dispatch(patchProductsSelectedFilters({}));
        dispatch(setProductsSortOrder(SortOrder.Asc));
        dispatch(setProductsSortTag(null));
        dispatch(setProductsHideZero(false));
        dispatch(setProductsShowOnlyMML(false));
    }, [dispatch, marketId]);

    const { visibleItems, loadMore } = useInfiniteScroll<Product>(
        searchFilteredProducts,
    );
    const lastIndex = visibleItems.length - 1;

    return (
        <>
            <Meta title="Додати товар" />
            <Header
                title="Додати товар"
                back
                rightActions={
                    <>
                        {!isRejectTask && (
                            <SVScanButton marketId={marketId} taskId={taskId} />
                        )}

                        <HeaderFilterButton
                            onClick={showDrawer}
                            filterCount={filterCount}
                        />
                    </>
                }
            />

            <StyledSearchWrapper>
                <Search
                    placeholder="Пошук"
                    defaultValue={searchQuery}
                    onSearch={onSearch}
                    allowClear
                    size="large"
                />
            </StyledSearchWrapper>

            <StyledContentContainer>
                {initialLoading ? (
                    <Spin size="large" />
                ) : (
                    <>
                        <Form>
                            {visibleItems.map((item, i) => {
                                const { barcode, name } = item;
                                const remainingCounter =
                                    currentFormValues?.[barcode];

                                return (
                                    <InventoryProductCard
                                        key={barcode}
                                        barcode={barcode}
                                        name={name}
                                        taskId={taskId}
                                        remainingCounter={remainingCounter}
                                        isLast={i === lastIndex}
                                        onView={loadMore}
                                    />
                                );
                            })}
                        </Form>

                        <ConfirmActionBtn
                            onConfirm={handleConfirmTask}
                            title="Завершити задачу"
                            description="Ви впевнені що хочете завершити виконання задачі?"
                            loading={sendLoading}
                        />
                    </>
                )}
            </StyledContentContainer>

            <OfflineFilterDrawer
                open={open}
                setOpen={setOpen}
                inventoryList={currentFormValues}
                setFilteredProducts={setSortedAndFilteredProducts}
            />
            {sendLoading && <Loading />}
        </>
    );
};
